import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { user, token, getAuthHeaders } = useAuth();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [branding, setBranding] = useState({
    name: 'GembaCoach',
    logo_url: null,
    primary_color: '#0E346E',
    accent_color: '#B98D32'
  });
  const [loading, setLoading] = useState(true);

  // Fetch tenant info when user is authenticated
  useEffect(() => {
    const fetchTenantInfo = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/current-tenant`, {
          headers: getAuthHeaders()
        });
        
        setTenantInfo(response.data.tenant);
        setBranding(response.data.branding);
        
        // Apply CSS custom properties for theming
        if (response.data.branding) {
          document.documentElement.style.setProperty('--primary-color', response.data.branding.primary_color || '#0E346E');
          document.documentElement.style.setProperty('--accent-color', response.data.branding.accent_color || '#B98D32');
        }
      } catch (error) {
        console.error('Error fetching tenant info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantInfo();
  }, [token, getAuthHeaders]);

  // Fetch tenant info by subdomain (for login page)
  const fetchTenantBySubdomain = async (subdomain) => {
    try {
      const response = await axios.get(`${API}/tenant/info?subdomain=${subdomain}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant by subdomain:', error);
      return null;
    }
  };

  const value = {
    tenantInfo,
    branding,
    loading,
    fetchTenantBySubdomain,
    isTenantUser: !!tenantInfo,
    tenantName: branding?.name || 'GembaCoach',
    tenantLogo: branding?.logo_url,
    primaryColor: branding?.primary_color || '#0E346E',
    accentColor: branding?.accent_color || '#B98D32'
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
